import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { required, emailFormatValid, composeValidators } from '../../../util/validators';
import { submitToWaitlist } from '../../../util/klaviyo';

import { Form, FieldTextInput, PrimaryButton } from '../../../components';

import css from './WaitlistForm.module.css';

const INTEREST_OPTIONS = [
  { key: 'renting', labelId: 'WaitlistForm.interest.renting' },
  { key: 'listing', labelId: 'WaitlistForm.interest.listing' },
  { key: 'baby_showers', labelId: 'WaitlistForm.interest.babyShowers' },
  { key: 'birthdays', labelId: 'WaitlistForm.interest.birthdays' },
  { key: 'dinner_parties', labelId: 'WaitlistForm.interest.dinnerParties' },
  { key: 'garden_parties', labelId: 'WaitlistForm.interest.gardenParties' },
  { key: 'bridal', labelId: 'WaitlistForm.interest.bridal' },
  { key: 'holiday_parties', labelId: 'WaitlistForm.interest.holidayParties' },
];

// Pull a 5-digit US zip code out of the search-page `address` query
// param. The autocomplete normally returns a city/state string, but
// when the user types a bare zip we want to pre-fill the form.
const extractZipFromAddress = address => {
  if (!address || typeof address !== 'string') return '';
  const trimmed = address.trim();
  return /^\d{5}$/.test(trimmed) ? trimmed : '';
};

/**
 * Waitlist form rendered inside the empty search-results state.
 * Captures email + optional details and posts to Klaviyo.
 */
const WaitlistForm = ({ initialZip = '', source = 'empty_search_results' }) => {
  const intl = useIntl();
  const [submitState, setSubmitState] = useState('idle');

  const onSubmit = async values => {
    setSubmitState('submitting');
    try {
      await submitToWaitlist({
        email: values.email?.trim(),
        firstName: values.firstName?.trim(),
        zip: values.zip?.trim(),
        interests: values.interests || [],
        source,
      });
      setSubmitState('success');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Waitlist submission failed:', e);
      setSubmitState('error');
    }
  };

  if (submitState === 'success') {
    return (
      <div className={css.successState} role="status" aria-live="polite">
        <div className={css.successIcon} aria-hidden="true">
          🎉
        </div>
        <h3 className={css.successHeadline}>
          <FormattedMessage id="WaitlistForm.success.headline" />
        </h3>
        <p className={css.successBody}>
          <FormattedMessage id="WaitlistForm.success.body" />
        </p>
      </div>
    );
  }

  return (
    <FinalForm
      initialValues={{ zip: initialZip, interests: [] }}
      onSubmit={onSubmit}
      render={({ handleSubmit, values, form, invalid }) => {
        const selectedInterests = values.interests || [];
        const toggleInterest = key => {
          const next = selectedInterests.includes(key)
            ? selectedInterests.filter(k => k !== key)
            : [...selectedInterests, key];
          form.change('interests', next);
        };

        const inProgress = submitState === 'submitting';
        const showError = submitState === 'error';

        return (
          <Form className={css.form} onSubmit={handleSubmit}>
            <FieldTextInput
              id="WaitlistForm.email"
              name="email"
              type="email"
              label={intl.formatMessage({ id: 'WaitlistForm.emailLabel' })}
              placeholder={intl.formatMessage({ id: 'WaitlistForm.emailPlaceholder' })}
              autoComplete="email"
              validate={composeValidators(
                required(intl.formatMessage({ id: 'WaitlistForm.emailRequired' })),
                emailFormatValid(intl.formatMessage({ id: 'WaitlistForm.emailInvalid' }))
              )}
            />

            <div className={css.row}>
              <FieldTextInput
                id="WaitlistForm.firstName"
                name="firstName"
                type="text"
                label={intl.formatMessage({ id: 'WaitlistForm.firstNameLabel' })}
                placeholder={intl.formatMessage({ id: 'WaitlistForm.firstNamePlaceholder' })}
                autoComplete="given-name"
              />
              <FieldTextInput
                id="WaitlistForm.zip"
                name="zip"
                type="text"
                inputMode="numeric"
                label={intl.formatMessage({ id: 'WaitlistForm.zipLabel' })}
                placeholder={intl.formatMessage({ id: 'WaitlistForm.zipPlaceholder' })}
                autoComplete="postal-code"
              />
            </div>

            <fieldset className={css.interestsFieldset}>
              <legend className={css.interestsLegend}>
                <FormattedMessage id="WaitlistForm.interestsLabel" />
              </legend>
              <div className={css.interestPills} role="group">
                {INTEREST_OPTIONS.map(opt => {
                  const checked = selectedInterests.includes(opt.key);
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      className={classNames(css.pill, { [css.pillSelected]: checked })}
                      aria-pressed={checked}
                      onClick={() => toggleInterest(opt.key)}
                    >
                      <FormattedMessage id={opt.labelId} />
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {showError ? (
              <p className={css.error} role="alert">
                <FormattedMessage id="WaitlistForm.submitError" />
              </p>
            ) : null}

            <PrimaryButton
              type="submit"
              className={css.submitButton}
              inProgress={inProgress}
              disabled={invalid || inProgress}
            >
              <FormattedMessage id="WaitlistForm.submit" />
            </PrimaryButton>
          </Form>
        );
      }}
    />
  );
};

export default WaitlistForm;
export { extractZipFromAddress };
